import React from 'react';
import { Box, Typography, Select, MenuItem, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { KeyboardArrowLeft as KeyboardArrowLeftIcon, KeyboardArrowRight as KeyboardArrowRightIcon } from '@mui/icons-material';

export interface TablePaginationProps {
    page: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (newPage: number) => void;
    onPageSizeChange: (event: any) => void;
    pageSizeOptions?: number[];
}

export const TablePagination: React.FC<TablePaginationProps> = ({
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [5, 10, 15, 20, 50],
}) => {
    const theme = useTheme();

    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const startIndex = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const endIndex = Math.min(page * pageSize, totalItems);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: theme.spacing(2, 3),
                borderTop: `1px solid ${theme.customColors.tableBorder}`,
            }}
        >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Showing {startIndex} to {endIndex} of {totalItems} results
            </Typography>
            <Box sx={{ display: 'flex', gap: theme.spacing(3), alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Rows per page:
                    </Typography>
                    <Select
                        value={pageSize}
                        onChange={onPageSizeChange}
                        size="small"
                        sx={{
                            height: 32,
                            fontSize: '0.875rem',
                            '& .MuiSelect-select': {
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.customColors.tableBorder,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                            },
                        }}
                    >
                        {pageSizeOptions.map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={page === 1}
                        onClick={() => onPageChange(page - 1)}
                        sx={{
                            minWidth: 'auto',
                            padding: theme.spacing(0.5),
                            borderColor: theme.customColors.tableBorder,
                            color: theme.palette.text.primary,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: 'transparent',
                            },
                            '&.Mui-disabled': {
                                borderColor: theme.customColors.tableBorder,
                            }
                        }}
                    >
                        <KeyboardArrowLeftIcon fontSize="small" />
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={page >= totalPages}
                        onClick={() => onPageChange(page + 1)}
                        sx={{
                            minWidth: 'auto',
                            padding: theme.spacing(0.5),
                            borderColor: theme.customColors.tableBorder,
                            color: theme.palette.text.primary,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: 'transparent',
                            },
                            '&.Mui-disabled': {
                                borderColor: theme.customColors.tableBorder,
                            }
                        }}
                    >
                        <KeyboardArrowRightIcon fontSize="small" />
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
